import React, { useEffect, useMemo } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
} from 'react-native';
import { DesktopSidePanel } from '@/components/common/admin/desktop/panel/DesktopSidePanel';
import {
  ResidentDetailsHeader,
  ResidentDetailsInfo,
  ResidentEditForm,
} from '@/components';
import {
  KPISummaryBar,
  TabSegment,
  type TabSegmentItem,
} from '@/components/shared/redesign';
import { useResidentDetails } from '@/hooks/admin/useResidentDetails';
import AdminErrorState from '@/components/common/admin/mobile/AdminErrorState';
import { RESIDENT_DETAILS_TABS } from '@/constants/admin/residents';
import { formatDateShort } from '@/utils/admin/date';
import { useThemedStyles, useThemeColors } from '@/hooks/shared/useThemeColors';
import { TransactionsBody } from '@/components/admin/resident/common/TransactionsBody';
import { useResidentTransactions } from '@/hooks/admin/useResidentTransactions';
import { SVG_COMPONENTS } from '@/assets/admin';
import { ResidentDetailsSidePanelSkeleton } from './ResidentDetailsSidePanelSkeleton';
import { TransactionHistorySkeleton } from '@/components/common/skeleton';

type ResidentTabKey =
  | typeof RESIDENT_DETAILS_TABS.GENERAL
  | 'Transactions'
  | typeof RESIDENT_DETAILS_TABS.EDIT;

interface ResidentDetailsSidePanelProps {
  visible: boolean;
  onClose: () => void;
  accountId: number | null;
  leaseId: number | null;
  mode?: 'account' | 'resident';
}

export const ResidentDetailsSidePanel: React.FC<ResidentDetailsSidePanelProps> = ({
  visible,
  onClose,
  accountId,
  leaseId,
  mode = 'resident',
}) => {
  const styles = useStyles();
  const { colors } = useThemeColors();
  const hasParams = !!accountId && !!leaseId;

  const {
    residentDetails,
    isLoading,
    error,
    enhancedError,
    refetch,
    retryCount,
    canRetry,
  } = useResidentDetails(
    hasParams
      ? { accountId: accountId as number, leaseId: leaseId as number, mode }
      : ({ accountId: 0, leaseId: 0, mode } as any),
  );

  const { transactions: txList, isLoading: txLoading } = useResidentTransactions({
    accountId: hasParams ? (accountId as number) : 0,
    leaseId: hasParams ? (leaseId as number) : 0,
    autoFetch: hasParams,
  });

  const tabItems: TabSegmentItem<ResidentTabKey>[] = [
    { key: RESIDENT_DETAILS_TABS.GENERAL, label: 'Overview' },
    { key: 'Transactions', label: 'Transactions' },
    { key: RESIDENT_DETAILS_TABS.EDIT, label: 'Edit' },
  ];
  const [activeTab, setActiveTab] = React.useState<ResidentTabKey>(
    RESIDENT_DETAILS_TABS.GENERAL,
  );

  useEffect(() => {
    if (!visible) setActiveTab(RESIDENT_DETAILS_TABS.GENERAL);
  }, [visible]);

  const kpiTiles = useMemo(() => {
    if (!residentDetails) return [];
    const b = residentDetails.balanceDue;
    const last = residentDetails.lastPaymentInfo;
    const months = residentDetails.endDate
      ? Math.max(
          0,
          Math.round(
            (new Date(residentDetails.endDate).getTime() - Date.now()) /
              (1000 * 60 * 60 * 24 * 30),
          ),
        )
      : null;
    const roommates = residentDetails.roommates ?? [];
    const roommateNames = roommates
      .slice(0, 3)
      .map((r: any) => r.firstName ?? r.data?.account?.firstName)
      .filter(Boolean)
      .join(' · ');
    return [
      {
        id: 'balance',
        label: 'Balance Due',
        value: b != null ? `$${b.toFixed(2)}` : '—',
        trend:
          b === 0
            ? { tone: 'success' as const, text: 'Paid' }
            : b && b > 0
            ? { tone: 'warn' as const, text: 'Past due' }
            : undefined,
      },
      {
        id: 'lastPayment',
        label: 'Last Payment',
        value: last ? formatDateShort(new Date(last.date)) : '—',
        caption: last
          ? `$${last.amount.toFixed(2)} · ${last.settlementStatus ?? 'cleared'}`
          : undefined,
        captionFallback: 'No payments on file',
      },
      {
        id: 'leaseEnds',
        label: 'Lease Ends',
        value: residentDetails.endDate
          ? formatDateShort(new Date(residentDetails.endDate))
          : '—',
        caption: months != null ? `${months} mo remaining` : undefined,
        captionFallback: 'Month-to-month',
      },
      {
        id: 'roommates',
        label: 'Roommates',
        value: String(roommates.length),
        caption: roommateNames || undefined,
        captionFallback: 'Living alone',
      },
    ];
  }, [residentDetails]);

  const goToTransactions = () => {
    setActiveTab('Transactions');
  };

  return (
    <DesktopSidePanel visible={visible} onClose={onClose} width={720}>
      <View style={styles.container}>
        <TouchableOpacity
          onPress={onClose}
          style={styles.closeButton}
          accessibilityRole="button"
          accessibilityLabel="Close"
          hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
        >
          <SVG_COMPONENTS.X_CIRCLE
            width={24}
            height={24}
            color={colors.text.primary}
          />
        </TouchableOpacity>
        {!hasParams ? null : isLoading && !residentDetails ? (
          <ResidentDetailsSidePanelSkeleton />
        ) : error && !residentDetails ? (
          <AdminErrorState
            title="Unable to Load Resident Details"
            message={
              enhancedError?.userFriendlyMessage ?? 'Something went wrong.'
            }
            retryCount={retryCount}
            canRetry={canRetry}
            onRetry={refetch}
          />
        ) : residentDetails ? (
          <>
            <ResidentDetailsHeader resident={residentDetails as any} />
            <KPISummaryBar tiles={kpiTiles as any} />
            <View style={styles.tabsRow}>
              <TabSegment
                items={tabItems}
                selectedKey={activeTab}
                onChange={setActiveTab}
              />
            </View>
            {activeTab === RESIDENT_DETAILS_TABS.GENERAL ? (
              <ScrollView showsVerticalScrollIndicator={false}>
                <ResidentDetailsInfo
                  resident={residentDetails as any}
                  leaseCustomerId={accountId as number}
                  leaseId={leaseId as number}
                  onViewAllTransactions={goToTransactions}
                />
              </ScrollView>
            ) : activeTab === 'Transactions' ? (
              <ScrollView showsVerticalScrollIndicator={false}>
                {txLoading ? (
                  <TransactionHistorySkeleton variant="table" count={5} showHeader />
                ) : (
                  <TransactionsBody transactions={txList} />
                )}
              </ScrollView>
            ) : (
              <ScrollView showsVerticalScrollIndicator={false}>
                <ResidentEditForm
                  accountId={accountId as number}
                  residentDetails={residentDetails as any}
                />
              </ScrollView>
            )}
          </>
        ) : null}
      </View>
    </DesktopSidePanel>
  );
};

const useStyles = () =>
  useThemedStyles(() =>
    StyleSheet.create({
      container: { flex: 1, padding: 16 },
      closeButton: {
        position: 'absolute',
        top: 12,
        right: 12,
        zIndex: 10,
        padding: 6,
      },
      tabsRow: { marginTop: 14, marginBottom: 12 },
    }),
  );
